import { styled } from '@mui/material/styles';
import { Badge, Box, Paper, Stack } from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import EditLocationAltOuttlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import { useState } from 'react';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as clientSlice from '../../../data/client.slice';
import SelectModelsWizard from './SelectModelsWizard';
import ProgramDialog from './ProgramDialog';
import ServiceAreaDialog from './ServiceAreaDialog';
import { useNavigate } from 'react-router-dom';
import ServiceLevelsWizard from './ServiceLevelsWizard';
import TransportationBenefitDialog from './transportation-benefit/TransportationBenefitDialog';
import ServicePlanDialog from './ServicePlanDialog';

const PREFIX = 'OnboardingTasks';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  badge: `${PREFIX}-badge`,
  taskContent: `${PREFIX}-taskContent`,
  taskIcon: `${PREFIX}-taskIcon`,
  taskText: `${PREFIX}-taskText`,
  taskForward: `${PREFIX}-taskForward`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    width: '688px',
  },

  [`& .${classes.title}`]: {
    display: 'flex',
    fontFamily: 'Bitter',
    fontSize: 30,
    fontWeight: 700,
    gap: theme.spacing(0.5),
  },

  [`& .${classes.badge}`]: {
    display: 'flex',
    height: '24px',
    padding: '0 6.5px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '64px',
    background: 'var(--Light-Primary-Shades-8p, rgba(25, 124, 179, 0.08))',

    color: 'var(--Light-Main, #197CB3)',
    textAlign: 'center',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '157%',
    letterSpacing: '0.1px',
  },

  [`& .${classes.taskContent}`]: {
    display: 'flex',
    padding: '16px 16px 16px 24px',
    alignItems: 'center',
    gap: '16px',
    alignSelf: 'stretch',
    borderRadius: '8px',
    background:
      'var(--Light-Secondary-Shades-4p, linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF)',

    color: 'var(--Light-Text-Primary, rgba(1, 16, 18, 0.90))',
    fontFeatureSettings: "'liga' off, 'clig' off",
    /* Typography/H3 */
    fontFamily: 'Bitter',
    fontSize: '21px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '32px',
    cursor: 'pointer',
  },

  [`& .${classes.taskIcon}`]: {
    color: 'rgba(1, 16, 18, 0.54)',
  },

  [`& .${classes.taskText}`]: {
    flexGrow: 1,
  },

  [`& .${classes.taskForward}`]: {
    display: 'flex',
    width: '16px',
    height: '16px',
    padding: '14px',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    borderRadius: '99px',
    background: 'var(--light-action-hover-4-p, rgba(1, 16, 18, 0.04))',
    color: 'rgba(1, 16, 18, 0.54)',
  },
}));

interface TaskItemProps {
  id: any;
  icon: any;
  text: string;
  onClickHandler: (open: boolean) => void;
}

function TaskItem(props: TaskItemProps) {
  const { id, icon: Icon, text } = props;
  return (
    <Box
      key={id}
      className={classes.taskContent}
      onClick={() => props.onClickHandler(true)}>
      {<Icon fontSize="large" className={classes.taskIcon} />}
      <Box className={classes.taskText}>{text}</Box>
      <ArrowForwardIosRoundedIcon className={classes.taskForward} />
    </Box>
  );
}

interface OnboardingTaskProps {
  clientBC: string;
}

export default function OnboardingTasks({ clientBC }: OnboardingTaskProps) {
  const t = useCommonTranslation();
  const navigate = useNavigate();

  const [selectModelsWizardOpen, setSelectModelsWizardOpen] = useState(false);
  const [serviceLevelsWizardOpen, setServiceLevelsWizardOpen] = useState(false);
  const client = useAppSelector(clientSlice.selectCurrentClient);
  const serviceAreas = useAppSelector(clientSlice.selectServiceAreas);
  const programs = useAppSelector(clientSlice.selectPrograms);
  const transportationBenefits = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );
  const servicePlanList = useAppSelector(clientSlice.selectServicePlanList);

  const [serviceAreaDialogOpen, setServiceAreaDialogOpen] = useState(false);
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [transportationBenefitDialogOpen, setTransportationBenefitDialogOpen] =
    useState(false);
  const [servicePlanDialogOpen, setServicePlanDialogOpen] = useState(false);

  const tasks: {
    icon: any;
    text: string;
    visible: boolean;
    action: (open: boolean) => void;
  }[] = [
    {
      icon: CheckCircleOutlineOutlinedIcon,
      text: t('client.selectModels'),
      visible: true,
      action: setSelectModelsWizardOpen,
    },
    {
      icon: AdminPanelSettingsOutlinedIcon,
      text: t('client.setupAdministration'),
      visible: true,
      action: () => null,
    },
    {
      icon: EditLocationAltOuttlinedIcon,
      text: t('client.configureLocationIngestion'),
      visible: true,
      action: () => null,
    },
    {
      icon: AddCircleOutlineOutlinedIcon,
      text: t('client.addServiceLevels'),
      visible: true,
      action: setServiceLevelsWizardOpen,
    },
    {
      icon: BuildCircleOutlinedIcon,
      text: t('client.setupServiceAreas'),
      // TODO: this should only become visible when the selectModels task is complete but for now we
      //       always show it so it can be tested.
      visible: !serviceAreas.length,
      action: setServiceAreaDialogOpen,
    },
    {
      icon: AddCircleOutlineOutlinedIcon,
      text: t('client.addProgram'),
      visible: !programs.length,
      action: () => setProgramDialogOpen(true),
    },
    {
      icon: AddCircleOutlineOutlinedIcon,
      text: t('client.addTransportationBenefit'),
      visible: programs.length > 0 && !transportationBenefits.length,
      action: () => setTransportationBenefitDialogOpen(true),
    },
    {
      icon: AddCircleOutlineOutlinedIcon,
      text: t('client.addServicePlan'),
      visible:
        transportationBenefits.length > 0 &&
        !servicePlanList?.servicePlans.length,
      action: () => setServicePlanDialogOpen(true),
    },
  ];

  const taskCount = () => {
    return tasks.reduce((cur, task) => (task.visible ? ++cur : cur), 0);
  };

  const handleServiceAreaDialogClose = () => {
    setServiceAreaDialogOpen(false);
    if (!!serviceAreas.length) {
      navigate(`/clients/${clientBC}/serviceAreas`);
      const home = document.querySelector('.Home-content');
      home?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectModelsWizardClose = () => {
    setSelectModelsWizardOpen(false);
  };

  const handleServiceLevelsWizardClose = () => {
    setServiceLevelsWizardOpen(false);
  };

  const handleProgramDialogClose = () => {
    setProgramDialogOpen(false);
  };

  const handleTransportationBenefitDialogClose = () => {
    setTransportationBenefitDialogOpen(false);
  };

  const handleServicePlanDialogClose = () => {
    setServicePlanDialogOpen(false);
  };

  const onSubmitClient = () => {
    // TODO: implement this
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.title}>
        {t('client.tasks')}{' '}
        <Badge className={classes.badge}>{taskCount()}</Badge>
      </Box>
      <Stack spacing={2}>
        {tasks.map((task, idx) => {
          return (
            task.visible && (
              <TaskItem
                id={idx}
                key={idx}
                icon={task.icon}
                text={task.text}
                onClickHandler={task.action}
              />
            )
          );
        })}
      </Stack>

      <SelectModelsWizard
        open={selectModelsWizardOpen}
        close={handleSelectModelsWizardClose}
      />
      <ServiceAreaDialog
        title={t('client.addServiceArea')}
        clientBC={clientBC}
        open={serviceAreaDialogOpen}
        close={handleServiceAreaDialogClose}
      />
      <ServiceLevelsWizard
        open={serviceLevelsWizardOpen}
        close={handleServiceLevelsWizardClose}
      />
      <ProgramDialog
        title={t('client.addProgram')}
        client={client!}
        open={programDialogOpen}
        close={handleProgramDialogClose}
      />
      <TransportationBenefitDialog
        title={t('client.addTransportationBenefit')}
        clientBC={clientBC}
        open={transportationBenefitDialogOpen}
        close={handleTransportationBenefitDialogClose}
      />
      <ServicePlanDialog
        title={t('client.addServicePlan')}
        open={servicePlanDialogOpen}
        client={client!}
        programs={programs}
        close={handleServicePlanDialogClose}
      />
    </StyledBox>
  );
}
